import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  checkStorageStatus,
  setStorageStatus,
  firstLoginComplete,
} from '../../utils/services/authAPI';
import { useSessionContext } from '../../context/sessionContext';
import { Button, Icon } from 'antd';
import { LoaderScreenStyles } from './styles';
import { ShortMsgCheckIcon, WarningIcon } from '../../images/svgIcons';
import { withTranslation } from 'react-i18next';
import { SAAS_SETUP_LOADER } from './translationValues';

const NAMESPACE = ['SAAS_SETUP_LOADER'];

const SaasSetupLoader = ({ t, createDefaultTemplate, onSuccess, isOpenDesign = false }) => {
  const organisationSelector = useCallback(store => ({ ...store }), []);
  const [organisationDetails, setContext] = useSessionContext(organisationSelector);

  const [showLoader, setShowLoader] = useState(false);

  const states = [
    {
      id: 1,
      state: t(SAAS_SETUP_LOADER.LOADING_STATE_ONE.key, SAAS_SETUP_LOADER.LOADING_STATE_ONE.value),
    },
    {
      id: 2,
      state: t(SAAS_SETUP_LOADER.LOADING_STATE_TWO.key, SAAS_SETUP_LOADER.LOADING_STATE_TWO.value),
    },
    {
      id: 3,
      state: t(
        SAAS_SETUP_LOADER.LOADING_STATE_THREE.key,
        SAAS_SETUP_LOADER.LOADING_STATE_THREE.value,
      ),
    },
    {
      id: 4,
      state: t(
        SAAS_SETUP_LOADER.LOADING_STATE_FOUR.key,
        SAAS_SETUP_LOADER.LOADING_STATE_FOUR.value,
      ),
    },
  ];
  const [loadingState, setLoadingState] = useState(0);

  const timeoutRef = useRef(0);
  const timerId = useRef(null);
  const completedRef = useRef(false);
  const redirectRef = useRef(true);
  useEffect(() => {
    const run = async () => {
      const firstLogin = organisationDetails?.accessTokenPayload?.firstLogin;
      if (firstLogin && loadingState === 0) {
        const response = await checkStorageStatus();
        if (response?.storageCreated) {
          if (response?.setup !== 'completed') {
            setStorageStatus({ setup: 'completed' });
          }
          setContext({ organizationSetup: 'completed' });
          firstLoginComplete();
        } else {
          setShowLoader(true);
          timeoutRef.current = 20000;
          setLoadingState(prev => prev + 1);
        }
      } else if (loadingState === 1 || loadingState === 2 || loadingState === 3) {
        timerId.current = setTimeout(async () => {
          if (!completedRef.current) {
            const response = await checkStorageStatus();
            if (response?.storageCreated) {
              completedRef.current = true;
              timeoutRef.current = 5000;
            } else if (loadingState === 3) {
              setStorageStatus({ setup: 'timeout' });
              setContext({ organizationSetup: 'failure' });
              return setLoadingState(6);
            }
          }
          setLoadingState(prev => prev + 1);
        }, timeoutRef.current);
      } else if (loadingState === 4) {
        const response = await createDefaultTemplate();
        if (!response) {
          redirectRef.current = false;
        }
        setLoadingState(prev => prev + 1);
      } else if (loadingState === 5) {
        timerId.current = setTimeout(() => {
          firstLoginComplete();
          setStorageStatus({ setup: 'completed' });
          setContext({ organizationSetup: 'completed' });
          setShowLoader(false);
          if (redirectRef.current || isOpenDesign) onSuccess();
        }, 3000);
      }
    };
    run();

    return () => {
      if (timerId.current !== null) clearTimeout(timerId.current);
    };
  }, [loadingState]);

  return (
    <LoaderScreenStyles
      visible={showLoader}
      footer={null}
      closable={loadingState === 6}
      onCancel={() => setShowLoader(false)}
      centered
      loadingComplete={loadingState === 5 || loadingState === 6}
      maskClosable={false}
    >
      <header>
        {loadingState === 5 || loadingState === 6 ? (
          <div className="setup-complete">
            {loadingState === 5 ? (
              <>
                <ShortMsgCheckIcon size={65} fillColor={'#8c51ff'} />
                <h3>
                  {t(
                    SAAS_SETUP_LOADER.SUCCESS_MESSAGE.key,
                    SAAS_SETUP_LOADER.SUCCESS_MESSAGE.value,
                  )}
                </h3>
              </>
            ) : (
              <>
                <WarningIcon width={65} height={65} pathFill="#c5393a" fill="#c5393a" />
                <h3>
                  {t(SAAS_SETUP_LOADER.ERROR_MESSAGE.key, SAAS_SETUP_LOADER.ERROR_MESSAGE.value)}
                </h3>
                <Button
                  onClick={() => {
                    completedRef.current = false;
                    setLoadingState(3);
                  }}
                >
                  {t(SAAS_SETUP_LOADER.RETRY_BUTTON.key, SAAS_SETUP_LOADER.RETRY_BUTTON.value)}
                </Button>
              </>
            )}
          </div>
        ) : (
          <h2>{t(SAAS_SETUP_LOADER.MODAL_TITLE.key, SAAS_SETUP_LOADER.MODAL_TITLE.value)}</h2>
        )}
      </header>
      <main>
        <div className="check-list">
          {states.map((state, index) => {
            return (
              <div className={`list-item ${state.id === loadingState ? 'completed' : ''}`}>
                <div className="icon">
                  {state.id < loadingState ? (
                    <Icon type="check" />
                  ) : (
                    state.id === loadingState && <Icon type="loading" />
                  )}
                </div>
                <label>{state.state}</label>
              </div>
            );
          })}
        </div>
      </main>
    </LoaderScreenStyles>
  );
};

export default withTranslation(NAMESPACE)(SaasSetupLoader);
